"use client";

import { Breadcrumbs } from "@/components/common/breadcrumbs";
import { CallToAction } from "@/components/common/call-to-action";
import { SeoHeading } from "@/components/common/seo-heading";

import { Section } from "@/components/section";
import { Text } from "@/components/ui/text";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "next/image";
import { cn } from "@/lib/utils";

import { usePopup } from "@/providers/popup-provider";

type BannerWithBackgroundImageContentType = {
  backgroundImagePath?: string;
  primaryButtonAction?: string;
  primaryButtonText?: string;
  secondaryButtonAction?: string;
  secondaryButtonText?: string;
  seoHeading?: string;
  heading: string;
  paragraph?: string;
  breadcrumbs: BreadcrumbsType[];
  serviceId?: number;
};

type BannerWithBackgroundImageProps = {
  section: PageSections;
  data: BannerWithBackgroundImageContentType;
};
const BannerWithBackgroundImage = (props: BannerWithBackgroundImageProps) => {
  const { section, data } = props;

  const { openPopup } = usePopup();

  const hasSecondaryPopup = data.secondaryButtonAction?.endsWith("_POPUP") ?? false;

  return (
    <Section
      id={section.sectionId}
      className={cn(section.sectionClass, "bg-background relative overflow-hidden")}
      bgImage={data.backgroundImagePath}
      bgImageProps={{
        loading: "eager",
        className: "hidden sm:block dark:blur-xl",
      }}
    >
      {/* Dark mode decorator */}
      <div className="from-background absolute inset-0 -z-1 hidden bg-linear-[60deg] to-transparent drop-shadow-2xl sm:from-60% dark:block" />

      <div className="grid grid-cols-12 items-center gap-5">
        <div className="col-span-12 sm:col-span-8">
          <div className="mb-10 sm:text-white">
            <SeoHeading as="h1" heading={data.heading} seoHeading={data.seoHeading} />
            {data.paragraph && <Text>{data.paragraph}</Text>}
          </div>
          <CallToAction
            size="lg"
            onSaveText={data.primaryButtonText}
            onSaveLink={data.primaryButtonAction}
            onCancelText={data.secondaryButtonText}
            onCancelLink={!hasSecondaryPopup ? data.secondaryButtonAction : undefined}
            onCancel={() => {
              if (hasSecondaryPopup) {
                openPopup({
                  type: "download-brochure",
                  data: {
                    serviceId: data?.serviceId,
                  },
                });
              }
            }}
          />
        </div>
        <div className="col-span-12 hidden sm:col-span-4 md:block">
          {data.backgroundImagePath && (
            <div className="group drop-shadow-primary overflow-hidden rounded-3xl bg-white/10 p-3 opacity-0 drop-shadow-xl dark:opacity-100">
              <AspectRatio
                ratio={1 / 1}
                className="h-full w-full transform overflow-hidden rounded-2xl transition-transform duration-700 group-hover:scale-110"
              >
                <Image
                  src={data.backgroundImagePath}
                  quality={100}
                  alt={data.heading ?? "background with bg image"}
                  className="transform object-cover object-right bg-blend-color transition-transform duration-700 group-hover:scale-[1.03]"
                  fill
                  sizes="400px"
                  loading="eager"
                  fetchPriority="high"
                />
              </AspectRatio>
            </div>
          )}
        </div>
      </div>
      <Breadcrumbs
        breadcrumbs={data.breadcrumbs}
        className="sm:absolute sm:inline-flex sm:rounded-2xl sm:bg-white/75 sm:px-4 sm:py-1 dark:bg-transparent"
      />
    </Section>
  );
};

export default BannerWithBackgroundImage;
